import { useEriCounties } from 'hooks/map/useEriCounties';
import type { ArcgisMapView } from './arcgisView';
import {
  ERI_CLASS_BREAKS,
  ERI_FIELD,
  ERI_LAYER_ID,
  buildClassBreaksRenderer,
} from './outageLayerStyles';
import {
  useCountyChoroplethLayerSync,
  type FieldInfoJson,
} from './useCountyChoroplethLayerSync';

const RENDERER = buildClassBreaksRenderer(ERI_CLASS_BREAKS, ERI_FIELD);

const FIELD_INFOS: FieldInfoJson[] = [
  { fieldName: 'countyName', label: 'County' },
  { fieldName: 'stateName', label: 'State' },
  { fieldName: 'eriMean', label: 'ERI level (mean)', format: { places: 2 } },
  { fieldName: 'eri10', label: 'ERI 10th percentile', format: { places: 2 } },
  { fieldName: 'eri25', label: 'ERI 25th percentile', format: { places: 2 } },
  { fieldName: 'eri75', label: 'ERI 75th percentile', format: { places: 2 } },
  { fieldName: 'eri90', label: 'ERI 90th percentile', format: { places: 2 } },
  { fieldName: 'maxWind', label: 'Max wind', format: { places: 1 } },
  { fieldName: 'awetsnMean', label: 'Wet snow (mean)', format: { places: 2 } },
  {
    fieldName: 'framThickMean',
    label: 'Ice thickness (mean)',
    format: { places: 2 },
  },
  { fieldName: 'thunderstorm', label: 'Thunderstorm', format: { places: 2 } },
  { fieldName: 'timestamp', label: 'Forecast hour' },
];

/**
 * Keeps the "Outage Prediction (ERI)" county choropleth on the map in sync with
 * the latest model run for the selected forecast step. Data is only fetched
 * while the layer is visible.
 */
export function useEriLayerSync({
  mapView,
  isVisible,
  step,
}: {
  mapView: ArcgisMapView | null;
  isVisible: boolean;
  /**
   * Requested forecast step (ISO) or null for the server default (next upcoming
   * hour).
   */
  step: string | null;
}) {
  const { data } = useEriCounties(isVisible, step);

  useCountyChoroplethLayerSync({
    mapView,
    layerId: ERI_LAYER_ID,
    title: 'Outage Prediction (ERI)',
    isVisible,
    featureCollection: data?.featureCollection,
    renderer: RENDERER,
    popupTitle: '{countyName}, {stateName} — ERI {eriMean}',
    fieldInfos: FIELD_INFOS,
  });
}
